/**
 * PRODUCTION LOGGER UTILITY
 * File: src/utils/logger.js
 * 
 * Replace all console.log/error/warn with this logger
 * Automatically removes logs in production
 * 
 * Usage:
 * import logger from './utils/logger';
 * logger.log('Message', data);
 * logger.error('Error message', error);
 * logger.warn('Warning message');
 */

class Logger {
  constructor() {
    this.logBuffer = [];
    this.maxLogs = 100;
  }

  /**
   * Standard log - buffered only (no console in production)
   */
  log(message, data = null) {
    // Console logging removed for production security
    this.addToBuffer('log', message, data);
  }

  /**
   * Error logging - buffered only (no console in production)
   */
  error(message, error = null) {
    // Console error logging removed for production security
    this.addToBuffer('error', message, error);
    this.reportToService('error', message, error);
  }

  /**
   * Warning logging
   */
  warn(message, data = null) {
    if (this.isDevelopment) {
      // Console statement removed for production
    }
    this.addToBuffer('warn', message, data);
  }

  /**
   * Info logging
   */
  info(message, data = null) {
    if (this.isDevelopment) {
      // Console info removed for production
    }
    this.addToBuffer('info', message, data);
  }

  /**
   * Buffer logs for later analysis
   */
  addToBuffer(level, message, data) {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, level, message, data };

    this.logBuffer.push(logEntry);

    if (this.logBuffer.length > this.maxLogs) {
      this.logBuffer.shift();
    }
  }

  /**
   * Send errors to remote service (Sentry, Firebase Crashlytics, etc.)
   */
  reportToService(level, message, error) {
    if (process.env.NODE_ENV === 'production') {
      try {
        // Example: Send to Firebase Cloud Function or Sentry
        const errorData = {
          level,
          message,
          error: error?.toString() || 'Unknown error',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          // Don't include sensitive user data
        };

        // Implement your error tracking service here
        // fetch('/api/report-error', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(errorData)
        // }).catch(err => {
        //   // Fail silently - don't create error loop
        // });
      } catch (e) {
        // Fail silently
      }
    }
  }

  /**
   * Get buffered logs (useful for debugging)
   */
  getLogs() {
    return this.logBuffer;
  }

  /**
   * Clear log buffer
   */
  clearLogs() {
    this.logBuffer = [];
  }

  /**
   * Export logs as JSON (useful for support)
   */
  exportLogs() {
    return JSON.stringify(this.logBuffer, null, 2);
  }
}

const logger = new Logger();

// Prevent accidental console usage in production
// Prevent accidental console usage in production
if (process.env.NODE_ENV === 'production') {
  const isDebugMode = typeof window !== 'undefined' && localStorage.getItem('debug_mode') === 'true';

  if (!isDebugMode) {
    // Override global console to prevent any logs
    const noop = () => { };
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    // Keep error and warn for critical issues
    console.error = (...args) => {
      logger.error('Console error called', args);
    };
    console.warn = (...args) => {
      logger.warn('Console warn called', args);
    };
  } else {
    // In debug mode, we might still want to capture logs for the buffer even if we show them
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    console.log = (...args) => {
      logger.addToBuffer('log', args[0], args.slice(1));
      originalLog.apply(console, args);
    };
    console.error = (...args) => {
      logger.addToBuffer('error', args[0], args.slice(1));
      originalError.apply(console, args);
    };
    console.warn = (...args) => {
      logger.addToBuffer('warn', args[0], args.slice(1));
      originalWarn.apply(console, args);
    };
    console.info = (...args) => {
      logger.addToBuffer('info', args[0], args.slice(1));
      originalInfo.apply(console, args);
    };
  }
}

// Add global helper to toggle debug mode
if (typeof window !== 'undefined') {
  window.toggleDebugMode = (enable) => {
    if (enable) {
      localStorage.setItem('debug_mode', 'true');
      console.log('🚧 Debug mode ENABLED. Refresh the page to see logs.');
    } else {
      localStorage.removeItem('debug_mode');
      console.log('🔒 Debug mode DISABLED. Refresh the page to hide logs.');
    }
  };
}

export default logger;

